import {
  Alert,
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { LifelineApplicationDto, LifelineListItemDto } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { FilterPanel } from '../../components/FilterPanel';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { formatDate } from '../../lib/format';
import { api } from '../../lib/api';
import { useListState } from '../../lib/useListState';
import { customerPath } from '../customers/parts';
import { useLifelineApplications } from './api';

export type LifelineListMode = 'all' | 'completed' | 'incomplete' | 'rejected';

const MODES: Record<LifelineListMode, { title: string; code: string }> = {
  all: { title: 'All Lifeline applications', code: 'PC1165' },
  completed: { title: 'Completed Lifeline applications', code: 'PC1166' },
  incomplete: { title: 'Incomplete Lifeline applications', code: 'PC1167' },
  rejected: { title: 'Rejected Lifeline applications', code: 'PC1168' },
};

const VERIFIER_COLOR = { Approved: 'success', Pending: 'warning', Rejected: 'error' } as const;

const columns = (page: number, pageSize: number): Column<LifelineListItemDto>[] => [
  { key: '#', header: '#', width: 50, render: (_r, i) => rowNumber(i, page, pageSize) },
  {
    key: 'enrollmentId',
    header: 'Enrollment ID',
    render: (r) => (
      <MuiLink component={Link} to={`/lifeline/${r.id}`}>
        {r.enrollmentId}
      </MuiLink>
    ),
  },
  { key: 'name', header: 'Name', render: (r) => r.name },
  { key: 'phone', header: 'Primary Phone', render: (r) => r.primaryPhone },
  { key: 'state', header: 'State', render: (r) => r.state },
  { key: 'zip', header: 'Zip', render: (r) => r.zip },
  { key: 'program', header: 'Eligibility Program', render: (r) => r.eligibilityProgram },
  {
    key: 'verifier',
    header: 'National Verifier',
    render: (r) =>
      r.verifierStatus ? (
        <Chip size="small" color={VERIFIER_COLOR[r.verifierStatus]} label={r.verifierStatus} />
      ) : (
        ''
      ),
  },
  { key: 'progress', header: 'Step', render: (r) => r.progress },
  { key: 'plan', header: 'Plan name', render: (r) => r.planName },
  {
    key: 'customer',
    header: 'Customer ID',
    render: (r) =>
      r.customerRef ? (
        <MuiLink component={Link} to={customerPath(r.customerRef)}>
          {r.customerId}
        </MuiLink>
      ) : (
        ''
      ),
  },
  { key: 'created', header: 'Created', nowrap: true, render: (r) => formatDate(r.createdAt) },
];

/** The Lifeline lists (PC1165 to PC1168): one screen, the mode decides the title and the rows. */
export function LifelineListPage({ mode }: { mode: LifelineListMode }) {
  const config = MODES[mode];
  const list = useListState({ q: '', dateFrom: '', dateTo: '' }, 15);
  const apps = useLifelineApplications({ ...list.params, mode });
  return (
    <Box>
      <PageHeader
        title={config.title}
        code={config.code}
        actions={
          <Button component={Link} to="/lifeline/new" variant="contained">
            New application
          </Button>
        }
      />
      <FilterPanel onSearch={list.search} onReset={list.reset}>
        <TextField
          type="date"
          label="Date range: From"
          value={list.draft.dateFrom}
          onChange={(e) => list.setField('dateFrom', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label="Date range: To"
          value={list.draft.dateTo}
          onChange={(e) => list.setField('dateTo', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Search"
          placeholder="Name, phone, Enrollment ID, application ID"
          value={list.draft.q}
          onChange={(e) => list.setField('q', e.target.value)}
          sx={{ minWidth: 300 }}
        />
      </FilterPanel>
      <Typography sx={{ mb: 1 }}>Total record: {apps.data?.total ?? 0}</Typography>
      <DataTable
        label={config.title}
        columns={columns(list.page, list.pageSize)}
        rows={apps.data?.data}
        rowKey={(r) => r.id}
        loading={apps.isFetching}
        error={apps.error}
        onRetry={() => void apps.refetch()}
        page={list.page}
        pageSize={list.pageSize}
        total={apps.data?.total ?? 0}
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        emptyText="No record found"
      />
    </Box>
  );
}

/* ------------------------------ Search account ----------------------------- */

interface SearchResult {
  applications: LifelineListItemDto[];
  customers: Array<{
    id: string;
    customerId: number;
    enrollmentId: string;
    name: string;
    mdn: string;
    status: string;
    planName: string;
    state: string;
  }>;
}

/** "Search account": Lifeline applications and Lifeline customers by name, phone or ID. */
export function LifelineSearchPage() {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const result = useQuery({
    queryKey: ['lifeline', 'search', q],
    queryFn: () => api.get<SearchResult>('/lifeline/search', { q }),
    enabled: q.length > 0,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setQ(text.trim());
  };
  return (
    <Box>
      <PageHeader title="Search account" code="PC1165" />
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          label="Name, phone, Customer ID, Enrollment ID or application ID"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ minWidth: 380 }}
        />
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Box>
      {result.isFetching ? <LoadingState label="Searching…" /> : null}
      {result.isError ? (
        <ErrorState error={result.error} onRetry={() => void result.refetch()} />
      ) : null}
      {result.data ? (
        <>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Lifeline customers
          </Typography>
          <DataTable
            label="Lifeline customers"
            columns={[
              {
                key: 'customerId',
                header: 'Customer ID',
                render: (c) => (
                  <MuiLink component={Link} to={customerPath(c.id)}>
                    {c.customerId}
                  </MuiLink>
                ),
              },
              { key: 'name', header: 'Name', render: (c) => c.name },
              { key: 'mdn', header: 'MDN', render: (c) => c.mdn },
              { key: 'status', header: 'Status', render: (c) => c.status },
              { key: 'plan', header: 'Plan name', render: (c) => c.planName },
              { key: 'state', header: 'State', render: (c) => c.state },
            ]}
            rows={result.data.customers}
            rowKey={(c) => c.id}
            emptyText="No Lifeline customer found"
          />
          <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
            Lifeline applications
          </Typography>
          <DataTable
            label="Lifeline applications"
            columns={columns(1, 50)}
            rows={result.data.applications}
            rowKey={(r) => r.id}
            emptyText="No application found"
          />
        </>
      ) : null}
    </Box>
  );
}

/* -------------------------------- Check status ----------------------------- */

/** "Check status": where one application stands, by Enrollment ID or verifier application ID. */
export function LifelineStatusPage() {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const status = useQuery({
    queryKey: ['lifeline', 'status', q],
    queryFn: () =>
      api.get<LifelineApplicationDto & { progress: string }>('/lifeline/status', { q }),
    enabled: q.length > 0,
    retry: false,
  });
  const a = status.data;
  return (
    <Box>
      <PageHeader title="Check status" code="PC1165" />
      <Box
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setQ(text.trim());
        }}
        sx={{ display: 'flex', gap: 1, mb: 2 }}
      >
        <TextField
          label="Enrollment ID or application ID"
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <Button type="submit" variant="contained">
          Check status
        </Button>
      </Box>
      {status.isFetching ? <LoadingState label="Checking…" /> : null}
      {status.isError ? (
        <Alert severity="warning">No Lifeline application has this ID.</Alert>
      ) : null}
      {a ? (
        <Paper variant="outlined" sx={{ p: 2 }} aria-label="Application status">
          <Typography variant="h3" sx={{ mb: 1 }}>
            {a.enrollmentId}: {a.firstName} {a.lastName}
          </Typography>
          <Typography>Step: {a.progress}</Typography>
          <Typography>
            National Verifier:{' '}
            {a.verifier
              ? `${a.verifier.status}${a.verifier.reason ? ` (${a.verifier.reason})` : ''}`
              : 'not checked yet'}
          </Typography>
          {a.verifier?.applicationId ? (
            <Typography>Application ID: {a.verifier.applicationId}</Typography>
          ) : null}
          {a.plan ? <Typography>Plan: {a.plan.name}</Typography> : null}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button component={Link} to={`/lifeline/${a.id}`} variant="outlined">
              Open application
            </Button>
            {a.customerRef ? (
              <Button component={Link} to={customerPath(a.customerRef)} variant="outlined">
                Open customer {a.customerId}
              </Button>
            ) : null}
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}
